'use client';

import { ReactElement, useEffect } from 'react';
import useEnergyCalculatorStore, {
  CalculatorData,
} from '../store/use-energy-calculator-store';
import { ReferenceTariffType } from '@/app/types/reference-tariff-type';
import useOfferComparisonStore, {
  fetchAndUpdateOffer,
} from '@/app/store/use-offer-comparison-store';
import usePromoCodeStore from '../store/use-promo-code-store';
import { CustomerType } from '@/lib/offer-service/types/customer-type';
import useEegUserDataStore from '../store/use-eeg-user-data-store';
import { EegUserData } from '@/lib/eeg/types/user-data';
import useCustomerStore from '../store/use-customer-store';
import { useTranslations } from 'next-intl';
import { addToast } from '@heroui/react';

type AppInitializerProps = {
  bookingLink?: string;
  children: ReactElement;
  selfLink?: string;
  calculatorData?: CalculatorData & { currentTariffCost?: number };
  promoCode?: string;
  eegUserData?: EegUserData | null;
  customerTag: string;
  isGreensCustomer: boolean;
  isGermanCustomer: boolean;
  customerType?: CustomerType | null;
};

export default function AppInitializer({
  bookingLink,
  children,
  selfLink,
  calculatorData,
  promoCode,
  eegUserData,
  customerTag,
  isGreensCustomer,
  isGermanCustomer,
  customerType,
}: AppInitializerProps) {
  const t = useTranslations('offerCalculator');
  const applyPromoCode = usePromoCodeStore((state) => state.applyCode);
  const setCustomerType = useEnergyCalculatorStore(
    (state) => state.setCustomerType
  );
  const setCustomer = useCustomerStore.setState;
  const setEegUserData = useEegUserDataStore((s) => s.setUserData);

  useEffect(() => {
    if (!calculatorData) return;
    const size = calculatorData.size;
    if (!size) return;
    const isSizeNumeric = /\d/.test(size as string);
    if (!isSizeNumeric) {
      setCustomerType(CustomerType.Business);
    }
  }, [calculatorData]);

  useEffect(() => {
    if (!eegUserData) return;
    if (eegUserData?.Company) {
      const hasBusinessData =
        !!eegUserData.CompanyName?.trim() &&
        !!eegUserData.CompanyVatNumber?.trim();

      if (hasBusinessData) {
        setEegUserData(eegUserData);
      }
      setCustomerType(CustomerType.Business);
    } else {
      setEegUserData(eegUserData);
    }
  }, [eegUserData, setEegUserData]);

  useEffect(() => {
    if (promoCode) {
      applyPromoCode(promoCode);
    }

    useEnergyCalculatorStore.setState({
      bookingLink,
      selfLink,
    });
  }, [bookingLink, selfLink, promoCode]);

  useEffect(() => {
    if (customerType) {
      setCustomerType(customerType);
    }
  }, [customerType]);

  useEffect(() => {
    setCustomer({ customer: customerTag, isGreensCustomer, isGermanCustomer });
  }, [customerTag, isGreensCustomer, isGermanCustomer]);

  useEffect(() => {
    const {
      consumption,
      size,
      zipCode,
      city,
      street,
      houseNumber,
      gridOperator,
      gridAreaId,
      gridOperatorId,
      currentTariffCost,
    } = calculatorData || {};
    if (currentTariffCost) {
      useOfferComparisonStore
        .getState()
        .setCurrentTariffCost(currentTariffCost);
    }

    const calculatorInitialData = {
      consumption,
      size,
      zipCode,
      city,
      street,
      houseNumber,
      gridOperator,
      gridOperatorId,
      gridAreaId,
      calculationMode: currentTariffCost
        ? ReferenceTariffType.invoice
        : ReferenceTariffType.average,
    };

    const filteredData = Object.fromEntries(
      Object.entries(calculatorInitialData).filter(([, value]) =>
        Boolean(value)
      )
    );

    useEnergyCalculatorStore.setState(filteredData);
    fetchAndUpdateOffer();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const offerError = useOfferComparisonStore((state) => state.offerError);

  useEffect(() => {
    if (offerError === 'notFound') {
      addToast({
        title: t('error.offerNotFound.title'),
        description: t('error.offerNotFound.description'),
        color: 'danger',
      });
    }
  }, [offerError, t]);

  return children;
}
